import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import "./App.css";

// Import components
import Login from "./components/Login";
import RestaurantDashboard from "./components/RestaurantDashboard";
import AdminDashboard from "./components/AdminDashboard";
import CustomerMenu from "./components/CustomerMenu";
import KitchenDisplay from "./components/KitchenDisplay";
import PasswordReset from "./components/PasswordReset";
import SetProfilePassword from "./components/SetProfilePassword";
import { Toaster } from "sonner";

// Use environment variable or fallback to production URL if not available
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

console.log('🔧 Backend URL:', BACKEND_URL);
console.log('🔧 API Base URL:', API);

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API,
});

// Add auth interceptor with cache-busting
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Remove cache-control headers from requests to avoid CORS preflight issues
  // The backend will handle cache-busting via response headers
  
  // Add cache-busting query parameter to all requests
  const separator = config.url.includes('?') ? '&' : '?';
  config.url = `${config.url}${separator}_cb=${Date.now()}&_v=${Math.random()}`;
  
  return config;
});

// Add response interceptor to handle token expiration
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - auto logout
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      localStorage.removeItem('userId');
      
      // Redirect to login if not already there, but skip public routes
      const isPublicRoute = window.location.pathname.startsWith('/menu/') || 
                           window.location.pathname.startsWith('/kitchen/');
      
      if (window.location.pathname !== '/login' && !isPublicRoute) {
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userType, setUserType] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);

  // Force cache refresh on app load
  useEffect(() => {
    // Clear any service workers that might be caching old versions
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(function(registrations) {
        for(let registration of registrations) {
          registration.unregister();
        }
      });
    }
    
    // Force reload if version mismatch detected or if corrupted session detected
    const appVersion = document.querySelector('meta[name="app-version"]')?.content;
    const lastVersion = localStorage.getItem('app-version');
    
    // Check for corrupted session data (common after database cleanup)
    const token = localStorage.getItem('token');
    const hasCorruptedSession = token && (
      !localStorage.getItem('userType') || 
      !localStorage.getItem('userId') ||
      token.includes('undefined') ||
      token.length < 10
    );
    
    if ((lastVersion && lastVersion !== appVersion) || hasCorruptedSession) {
      console.log('App version changed or corrupted session detected, clearing cache...');
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear cookies as well
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
      
      if (hasCorruptedSession) {
        console.log('🔧 Corrupted session cleared - reloading...');
      }
      
      window.location.reload(true);
      return;
    }
    
    if (appVersion) {
      localStorage.setItem('app-version', appVersion);
    }
  }, []);

  useEffect(() => {
    // Check if user is already logged in
    const token = localStorage.getItem('token');
    const storedUserType = localStorage.getItem('userType');
    const storedUserId = localStorage.getItem('userId');
    
    let refreshInterval;
    
    if (token && storedUserType && storedUserId) {
      setIsAuthenticated(true);
      setUserType(storedUserType);
      setUserId(storedUserId);
      
      // Set up periodic token refresh (every 7 days)
      refreshInterval = setInterval(async () => {
        try {
          const response = await apiClient.post('/auth/refresh');
          localStorage.setItem('token', response.data.access_token);
          console.log('Token refreshed successfully');
        } catch (error) {
          console.log('Token refresh failed - user will be logged out');
          handleLogout();
        }
      }, 7 * 24 * 60 * 60 * 1000); // 7 days in milliseconds
    }
    
    // Always set loading to false after checking authentication
    setLoading(false);
    
    // Cleanup function
    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);
  
  // Separate timeout effect to handle loading issues
  useEffect(() => {
    if (!loading) return; // Don't set timeout if already loaded
    
    const loadingTimeout = setTimeout(() => {
      setLoadingError("Loading timeout - please try clearing cache");
      setLoading(false);
    }, 10000); // 10 second timeout
    
    return () => clearTimeout(loadingTimeout);
  }, [loading]);

  // Add emergency cache clear function (can be called from browser console)
  useEffect(() => {
    window.clearAppCache = () => {
      console.log('🧹 Clearing all app cache...');
      localStorage.clear();
      sessionStorage.clear();
      
      // Clear IndexedDB if available
      if (window.indexedDB) {
        indexedDB.databases().then(databases => {
          databases.forEach(db => {
            indexedDB.deleteDatabase(db.name);
          });
        });
      }
      
      // Clear service workers
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.unregister();
          });
        });
      }
      
      console.log('✅ Cache cleared! Reloading page...');
      window.location.reload(true);
    };
    
    console.log('💡 If experiencing loading issues, run: clearAppCache()');
  }, []);

  const handleLogin = (token, userType, userId) => {
    localStorage.setItem('token', token);
    localStorage.setItem('userType', userType);
    localStorage.setItem('userId', userId);
    setIsAuthenticated(true);
    setUserType(userType);
    setUserId(userId);
  };

  const handleLogout = async () => {
    try {
      // Call backend to invalidate all sessions
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.log('Logout API call failed, proceeding with local logout');
    }
    
    // Clear local storage regardless of API call result
    localStorage.removeItem('token');
    localStorage.removeItem('userType');
    localStorage.removeItem('userId');
    setIsAuthenticated(false);
    setUserType(null);
    setUserId(null);
  };

  if (loading && !loadingError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 mb-4">Loading application...</p>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-yellow-800 mb-3">
              <strong>If loading is taking too long:</strong>
            </p>
            <div className="space-y-2">
              <button 
                onClick={() => window.location.reload(true)}
                className="w-full bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700"
              >
                Hard Refresh Page
              </button>
              <button 
                onClick={() => {
                  localStorage.clear(); 
                  sessionStorage.clear(); 
                  window.location.reload(true);
                }}
                className="w-full bg-orange-600 text-white px-3 py-2 rounded text-sm hover:bg-orange-700"
              >
                Clear Cache & Reload
              </button>
            </div>
            <p className="text-xs text-yellow-700 mt-2">
              Try opening in an incognito/private window if issues persist
            </p>
          </div>
          
          <p className="text-xs text-gray-500">
            Loading timeout will trigger automatically in 10 seconds
          </p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Loading Issue Detected</h2>
          <p className="text-gray-600 mb-6">{loadingError}</p>
          <div className="space-y-3">
            <button 
              onClick={() => window.clearAppCache && window.clearAppCache()}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              Clear App Cache
            </button>
            <button 
              onClick={() => window.location.reload(true)}
              className="w-full bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
            >
              Hard Refresh Page
            </button>
            <p className="text-xs text-gray-500">
              If issues persist, try opening in an incognito/private window
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Router>
        <Routes>
          {/* Password Reset Route */}
          <Route 
            path="/reset-password" 
            element={<PasswordReset apiClient={apiClient} />} 
          />
          
          {/* Public Routes */}
          <Route 
            path="/menu/:restaurantId/:tableId" 
            element={<CustomerMenu apiClient={apiClient} />} 
          />
          <Route 
            path="/menu/:restaurantId/:tableId/*" 
            element={<CustomerMenu apiClient={apiClient} />} 
          />
          
          {/* Kitchen Display Route - Public for kitchen screens */}
          <Route 
            path="/kitchen/:restaurantId" 
            element={<KitchenDisplay />} 
          />
          
          {/* Profile Password Setup Route - Public */}
          <Route 
            path="/profile/set-password" 
            element={<SetProfilePassword apiClient={apiClient} />} 
          />
          
          {/* Authentication Route */}
          <Route 
            path="/login" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Login apiClient={apiClient} onLogin={handleLogin} />
              )
            } 
          />
          
          {/* Protected Dashboard Routes */}
          <Route 
            path="/dashboard" 
            element={
              isAuthenticated ? (
                userType === 'restaurant' ? (
                  <RestaurantDashboard 
                    apiClient={apiClient} 
                    userId={userId}
                    onLogout={handleLogout}
                  />
                ) : userType === 'admin' ? (
                  <AdminDashboard 
                    apiClient={apiClient} 
                    userId={userId}
                    onLogout={handleLogout}
                  />
                ) : (
                  <Navigate to="/login" replace />
                )
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
          
          {/* Default Route */}
          <Route 
            path="/" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <Navigate to="/login" replace />
              )
            } 
          />
        </Routes>
      </Router>
      
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;